import { BookmarkPlus, Library } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  CRITERION_IMPORTANCE_LABELS,
  CRITERION_IMPORTANCES,
  CRITERION_KIND_LABELS,
  CRITERION_KINDS,
  MAX_BUILDER_CRITERIA,
  criterionFromLibrary,
  defaultMinimum,
  importanceWeights,
  minimumRange,
  type CriterionImportance,
  type CriterionKind,
  type SavedCriterionView,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Popover, usePopover } from '../../../../components/Popover';
import { Switch } from '../../../../components/Switch';
import { EditableTable, type EditableColumn } from '../../../../components/editable-table/EditableTable';
import { errorMessage } from '../../../../lib/api-client';
import { useCanEdit } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { useSaveToLibrary } from '../../api/criteria-library-api';
import { CriteriaLibraryPicker } from './CriteriaLibraryPicker';
import styles from './builder.module.css';

// JOB-13's screening criteria (F5.21, JB-8…JB-10): one row per criterion, picked by importance
// and kind. Weights are worked out from importance (§L30) and shown read-only behind "Show
// weights"; nobody types one (O24).

const CRITERION_LABEL_HINT = "Name it, e.g. Master's degree";
/** An unnamed row is skipped when saving and scoring, so its weight cell says so. */
const NOT_COUNTED = 'Empty — not counted';

export interface CriterionRow {
  readonly id: string;
  readonly label: string;
  readonly importance: CriterionImportance;
  readonly kind: CriterionKind;
  readonly lookFor: readonly string[];
  readonly partlyCounts: readonly string[];
  readonly minLevel: number | null;
}

export function blankCriterionRow(): CriterionRow {
  return { id: crypto.randomUUID(), label: '', importance: 'important', kind: 'keywords', lookFor: [], partlyCounts: [], minLevel: null };
}

/**
 * §L30: a Years criterion is scored from the years column the import matched, never from CV text,
 * so it has no words to look for. Its Look-for and Partly-counts cells read "—"; any words saved
 * before stay on the row (switching back to another kind brings them back).
 */
export function looksForWords(kind: CriterionKind): boolean {
  return kind !== 'years';
}

/** Only named rows are saved; a new blank row waits until it's given a name. */
export function namedCriteria(rows: readonly CriterionRow[]): readonly CriterionRow[] {
  return rows.filter((row) => row.label.trim() !== '');
}

interface CriteriaTableProps {
  readonly rows: readonly CriterionRow[];
  /** Saves the whole table; resolves when saved, rejects (after rolling back) when not. */
  readonly onCommit: (next: readonly CriterionRow[]) => Promise<void>;
  /** A blank row exists only here until it has a name. */
  readonly onRowsChange: (next: readonly CriterionRow[]) => void;
  readonly isReadOnly: boolean;
}

export function CriteriaTable({ rows, onCommit, onRowsChange, isReadOnly }: CriteriaTableProps): ReactNode {
  const [isShowingWeights, setIsShowingWeights] = useState(false);
  // §L30's 0.7 words stay out of the way until wanted, and are never hidden once used (Start R5 Q2).
  const [isShowingPartly, setIsShowingPartly] = useState(() => rows.some((row) => row.partlyCounts.length > 0));
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const show = useToasts((toasts) => toasts.show);
  // F5.25 (ADR 0048): the library is for whoever builds jobs; saving never edits this job.
  const canUseLibrary = useCanEdit('job_config');
  const saveToLibrary = useSaveToLibrary();
  const { anchor: libraryAnchor, triggerRef: libraryTrigger, toggle: toggleLibrary, close: closeLibrary } = usePopover();
  const weights = useMemo(() => {
    const named = namedCriteria(rows);
    const worked = importanceWeights(named.map((row) => row.importance));
    return new Map(named.map((row, index) => [row.id, worked[index] ?? 0]));
  }, [rows]);

  const columns: readonly EditableColumn<CriterionRow>[] = [
    {
      key: 'label',
      header: 'What',
      width: isShowingWeights ? '22%' : '25%',
      // Reads as an example, never as a value someone typed (tester D2).
      cell: { kind: 'text', placeholder: CRITERION_LABEL_HINT, maxLength: 120, value: (row) => row.label, update: (row, label) => ({ ...row, label }) },
    },
    {
      key: 'kind',
      header: 'Kind',
      width: '18%',
      cell: {
        kind: 'select',
        options: CRITERION_KINDS.map((kind) => ({ value: kind, label: CRITERION_KIND_LABELS[kind] })),
        value: (row) => row.kind,
        update: (row, value) => {
          const kind = CRITERION_KINDS.find((candidate) => candidate === value) ?? row.kind;
          // A new kind brings its own sensible minimum (06-qol §7 smart defaults).
          return { ...row, kind, minLevel: defaultMinimum(kind) };
        },
      },
    },
    {
      key: 'importance',
      header: 'Importance',
      width: '18%',
      cell: {
        kind: 'select',
        options: CRITERION_IMPORTANCES.map((importance) => ({ value: importance, label: CRITERION_IMPORTANCE_LABELS[importance] })),
        value: (row) => row.importance,
        update: (row, value) => ({ ...row, importance: CRITERION_IMPORTANCES.find((candidate) => candidate === value) ?? row.importance }),
      },
    },
    {
      key: 'minimum',
      header: 'Minimum',
      width: '10%',
      cell: { kind: 'number', range: (row) => minimumRange(row.kind), value: (row) => row.minLevel, update: (row, minLevel) => ({ ...row, minLevel }) },
    },
    {
      key: 'lookFor',
      header: 'Look for',
      cell: {
        kind: 'tags',
        maxItems: 20,
        value: (row) => (looksForWords(row.kind) ? row.lookFor : []),
        update: (row, lookFor) => ({ ...row, lookFor }),
        placeholder: 'Optional',
      },
      isEditable: (row) => looksForWords(row.kind),
    },
    ...(isShowingPartly
      ? [
          {
            key: 'partlyCounts',
            header: 'Partly counts',
            cell: {
              kind: 'tags' as const,
              maxItems: 20,
              value: (row: CriterionRow) => (looksForWords(row.kind) ? row.partlyCounts : []),
              update: (row: CriterionRow, partlyCounts: readonly string[]) => ({ ...row, partlyCounts }),
              placeholder: 'Optional',
            },
            isEditable: (row: CriterionRow) => looksForWords(row.kind),
          },
        ]
      : []),
    ...(isShowingWeights
      ? [
          {
            key: 'weight',
            header: 'Weight',
            width: '9%',
            cell: { kind: 'display' as const, render: (row: CriterionRow) => weights.get(row.id) ?? <span className={styles.notCounted}>{NOT_COUNTED}</span> },
          },
        ]
      : []),
  ];

  function save(row: CriterionRow): void {
    saveToLibrary.mutate({ ...row, lookFor: [...row.lookFor], partlyCounts: [...row.partlyCounts] }, {
      onSuccess: (result) =>
        show({
          tone: 'success',
          message: `"${result.criterion.label}" saved to the library`,
          detail: result.isReplaced ? 'It replaces the one saved under the same name.' : 'Any job can now add it from "Add from saved".',
        }),
      onError: (cause) => show({ tone: 'danger', message: `"${row.label}" wasn't saved to the library`, detail: errorMessage(cause) }),
    });
  }

  /** Copies, never links (non-negotiable #4): each gets a new row id and is saved with the table. */
  function addFromLibrary(picked: readonly SavedCriterionView[]): void {
    closeLibrary();
    const copies = picked.slice(0, MAX_BUILDER_CRITERIA - namedCriteria(rows).length).map((saved) => criterionFromLibrary(saved, crypto.randomUUID()));
    if (copies.length === 0) return;
    void onCommit([...namedCriteria(rows), ...copies]).catch(() => undefined);
  }

  function replaceRow(next: CriterionRow): readonly CriterionRow[] {
    return rows.map((row) => (row.id === next.id ? next : row));
  }

  return (
    <div className={styles.tableSection}>
      <div className={styles.tableTools}>
        <p className={styles.help}>
          Must-have counts three times a nice-to-have. A candidate missing a Must-have is capped at 49 and flagged, never rejected for it.
          {isShowingPartly && ' A Look-for word counts in full; a Partly-counts word counts 70%.'}
        </p>
        <div className={styles.tableSwitches}>
          {canUseLibrary && !isReadOnly && (
            <Button ref={libraryTrigger} variant="outline" size="sm" icon={Library} disabled={rows.length >= MAX_BUILDER_CRITERIA} onClick={toggleLibrary}>
              Add from saved
            </Button>
          )}
          <Switch label="Partly-counts words" checked={isShowingPartly} onChange={setIsShowingPartly} />
          <Switch label="Show weights" checked={isShowingWeights} onChange={setIsShowingWeights} />
        </div>
      </div>
      <EditableTable
        label="Screening criteria"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        rowLabel={(row) => row.label || 'new criterion'}
        isReadOnly={isReadOnly}
        {...(canUseLibrary
          ? {
              rowActions: (row: CriterionRow) => [
                { label: 'Save to library', icon: <BookmarkPlus size={15} />, disabled: row.label.trim() === '', onSelect: () => save(row) },
              ],
            }
          : {})}
        emptyText="No criteria yet. Add what you want the ranking to look for, or add some you saved before."
        addLabel="Add criterion"
        isAddDisabled={rows.length >= MAX_BUILDER_CRITERIA}
        onAdd={() => onRowsChange([...rows, blankCriterionRow()])}
        onCommit={(change) => onCommit(replaceRow(change.row))}
        onMove={(from, to) => {
          const next = [...rows];
          const [moved] = next.splice(from, 1);
          if (!moved) return;
          next.splice(to, 0, moved);
          void onCommit(next).catch(() => undefined);
        }}
        onDelete={(rowKey) => {
          const index = rows.findIndex((row) => row.id === rowKey);
          const removed = rows[index];
          if (!removed) return;
          const next = rows.filter((row) => row.id !== rowKey);
          void onCommit(next).catch(() => undefined);
          showUndo(`${removed.label || 'Criterion'} removed`, () => {
            const restored = [...next];
            restored.splice(index, 0, removed);
            void onCommit(restored).catch(() => undefined);
          });
        }}
      />
      {rows.length >= MAX_BUILDER_CRITERIA && <p className={styles.help}>That's the most a job can have: {MAX_BUILDER_CRITERIA} criteria.</p>}
      <Popover anchor={libraryAnchor} onClose={closeLibrary} label="Add saved criteria" align="right" width={440}>
        <CriteriaLibraryPicker
          existingLabels={rows.map((row) => row.label)}
          room={MAX_BUILDER_CRITERIA - namedCriteria(rows).length}
          onAdd={addFromLibrary}
          onCancel={closeLibrary}
        />
      </Popover>
    </div>
  );
}

